import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import SignatureCapture from "@/components/SignatureCapture";
import { toast } from "sonner";
import { ShieldCheck, IdentificationCard, ArrowRight } from "@phosphor-icons/react";

/**
 * Onboarding for OAuth users:
 * 1) KVKK & company internal-rules acceptance (signature required).
 * 2) Profile-completion reminder → redirect to /settings.
 *
 * Both steps run only when the backend `/auth/onboarding/status` says they are needed.
 * They are entirely dismissible except the KVKK step is enforced (backend blocks nothing but
 * the user is nudged strongly every login until accepted).
 */
export default function OAuthOnboarding() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [kvkkOpen, setKvkkOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [sig, setSig] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Only fetch onboarding status when we have an authenticated user.
    if (!user?.id) return;
    api.get("/auth/onboarding/status")
      .then((r) => setStatus(r.data))
      .catch((e) => {
        // Silently ignore 401 (auth not yet resolved on public routes).
        if (e?.response?.status && e.response.status !== 401) {
          console.warn("onboarding status failed:", e);
        }
      });
  }, [user?.id]);

  useEffect(() => {
    if (!status) return;
    if (status.needs_kvkk) {
      setKvkkOpen(true);
    } else if (status.needs_profile && (status.google_linked || status.apple_linked)) {
      // Show profile reminder once per session
      const dismissedKey = `profile_reminder_dismissed_${user.id}`;
      if (!sessionStorage.getItem(dismissedKey)) {
        setProfileOpen(true);
      }
    }
  }, [status, user?.id]);

  const submitKvkk = async () => {
    if (!accepted) return toast.error("Lütfen sözleşmeyi kabul edin");
    if (!sig && !user?.signature) return toast.error("Lütfen imzanızı ekleyin");
    setSubmitting(true);
    try {
      await api.post("/auth/onboarding/accept-kvkk", { signature: sig || undefined });
      toast.success(t("kvkk.accepted"));
      setKvkkOpen(false);
      // reload status → possibly show profile modal next
      const { data } = await api.get("/auth/onboarding/status");
      setStatus(data);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSubmitting(false);
    }
  };

  const goSettings = () => {
    sessionStorage.setItem(`profile_reminder_dismissed_${user.id}`, "1");
    setProfileOpen(false);
    nav("/settings");
    // Scroll to profile card after navigation
    setTimeout(() => {
      const el = document.querySelector('[data-testid="profile-info-card"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  };
  const dismissProfile = () => {
    sessionStorage.setItem(`profile_reminder_dismissed_${user.id}`, "1");
    setProfileOpen(false);
  };

  return (
    <>
      {/* KVKK Wizard — enforced modal, cannot be closed without accepting */}
      <Dialog open={kvkkOpen} onOpenChange={() => {/* enforced */}}>
        <DialogContent
          className="max-w-2xl max-h-[92vh] overflow-y-auto"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="kvkk-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={22} weight="duotone" className="text-primary" />
              {t("kvkk.title")}
            </DialogTitle>
            <DialogDescription>{t("kvkk.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md border border-border bg-muted/30 p-4 max-h-64 overflow-y-auto text-sm leading-relaxed">
              <div className="font-semibold mb-2">{t("kvkk.body_title")}</div>
              <div className="text-muted-foreground whitespace-pre-line">{t("kvkk.body")}</div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer" data-testid="kvkk-accept-check">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
              <span className="text-sm">{t("kvkk.checkbox")}</span>
            </label>
            {!user?.signature && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t("kvkk.signature_label")}</div>
                <SignatureCapture onChange={setSig} testId="kvkk-signature" />
              </div>
            )}
            {user?.signature && (
              <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300 dark:border-emerald-700 p-3 text-xs text-emerald-900 dark:text-emerald-200">
                Kayıtlı imzanız otomatik olarak kullanılacaktır.
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={submitKvkk} disabled={submitting || !accepted} data-testid="kvkk-submit">
                {submitting ? "…" : t("kvkk.accept")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Completion Reminder — dismissible */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent data-testid="profile-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IdentificationCard size={22} weight="duotone" className="text-primary" />
              {t("profile.modal.title")}
            </DialogTitle>
            <DialogDescription>{t("profile.modal.body")}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 pt-2">
            {(status?.missing_fields || []).map((f) => (
              <Badge key={f.key} variant="secondary" data-testid={`missing-${f.key}`}>
                {f.label}
              </Badge>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={dismissProfile} data-testid="profile-later">{t("profile.modal.later")}</Button>
            <Button onClick={goSettings} data-testid="profile-go-settings">
              {t("profile.modal.go")} <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
